import { Dialog, Transition } from '@headlessui/react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';

import { queryClient } from '../../App';
import api from '../../services/apiSgft';
import { JourneyExtensionTypeAtom } from '../../state/JourneyExtensionTypeAtom';
import { Button } from '../atoms/Button';
import CloseButton from '../atoms/CloseButton';
import { DatePicker, DatePickerRange } from '../atoms/DatePicker';
import { journeyExtensionColumns } from '../atoms/JourneyExtensionColumns';
import Spinner from '../atoms/Spinner';
import SubtitleText from '../atoms/SubtitleText';
import TextComponent from '../atoms/TextComponent';
import ExpandableSubtitle from '../molecules/ExpandableSubtitle';
import JourneyExtensionCard from '../molecules/JourneyExtensionCard';
import Table from './Table';

interface Props {
	employeeId: number;
	modalIsOpen: boolean;
	handleClose: () => void;
}

export default function JourneyExtensionModal({
	employeeId,
	modalIsOpen,
	handleClose,
}: Props) {
	const [name, setName] = useState('');
	const [employeeNumber, setEmployeeNumber] = useState('');
	const [dates, setDates] = useState<DatePickerRange | undefined>({
		startDate: undefined,
		endDate: undefined,
	});
	const [updateData, setUpdateData] = useState(false);
	const [, setType] = useRecoilState(JourneyExtensionTypeAtom);
	const [canSave, setCanSave] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [editData, setEditData] = useState();
	const [data, setData] = useState([]);

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await api.get('/work-shift-extension/data', {
				params: {
					employeeId,
					startDate: dates?.startDate ? dates?.startDate : null,
					endDate: dates?.endDate ? dates?.endDate : null,
				},
			});

			const newData = response.data.workShiftExtension.map((data: any) => ({
				id: data.id,
				startDate: new Date(data.startDate).toISOString().slice(0, 10),
				motive: data.motive,
				justification: data.justification,
				canEditOrDelete: data.canEditOrDelete,
				canEditJustification: data.canEditJustification,
				canDeleteJustification: data.canDeleteJustification,
			}));

			setData(newData);
			setName(response.data.name);
			setEmployeeNumber(response.data.employee_number);
			setCanSave(response.data.canSave);
			setIsLoading(false);
			setUpdateData(false);
		} catch (e) {
			setIsLoading(false);
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	}, [employeeId, dates?.startDate, dates?.endDate]);

	const handleDelete = async (data: any) => {
		try {
			await api.delete(
				`/work-shift-extension?workShiftExtensionId=${Number(data.id)}`,
			);
			setUpdateData(true);
			setType('');
			queryClient.invalidateQueries(['team-structure']);
		} catch (e) {
			toast.error('Não foi possível deletar o sobreaviso', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	useEffect(() => {
		setType('');
	}, [modalIsOpen, setType]);

	const handleEdit = async (data: any) => {
		setType('edit');
		setEditData(data);
	};

	useEffect(() => {
		fetchData();
	}, [updateData, dates, fetchData]);

	return (
		<Transition appear show={true}>
			<Dialog className="relative z-50" onClose={handleClose}>
				<div className="fixed inset-0 flex w-screen items-center justify-center bg-black/30 p-4">
					<Transition.Child
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="max-h-[100vh] w-[50rem] flex-col justify-between overflow-auto overflow-y-scroll rounded-lg bg-white scrollbar-thin scrollbar-thumb-[#D9D9D9] scrollbar-thumb-rounded-full">
							<div className="w-full flex-col justify-between p-5">
								<div className="flex h-auto items-center justify-between">
									<div className="flex justify-between">
										<SubtitleText
											className="text-[20px]"
											subtitle={'Extensão de Jornada'}
										/>
									</div>
									<CloseButton onClick={handleClose} className="mt-[-1rem]" />
								</div>
								<div className="mt-5">
									<div className="mb-5 flex w-full justify-between">
										<TextComponent title="Nome" value={name} width="20rem" />
										<TextComponent
											title="Chapa"
											value={employeeNumber}
											width="10rem"
										/>
										<div className="w-[15rem]">
											<div className="font-graphie text-[12px] text-[#4A4A4A]">
												Datas
											</div>
											<DatePicker
												value={dates}
												onSelect={(dateRange) => setDates(dateRange)}
												className={{ button: 'h-8 w-[12rem]' }}
												showShortCuts
											/>
										</div>
									</div>
									<>
										<ExpandableSubtitle
											subtitle={'Extensões de Jornada Planejadas'}
										>
											{isLoading && (
												<div className="flex h-40 w-full items-center">
													<Spinner size={50} />
												</div>
											)}
											{data.length > 0 ? (
												<div className="mb-5">
													<Table
														columns={journeyExtensionColumns}
														data={data}
														isWhiteTable
														onEditTableRow={handleEdit}
														onDeleteTableRow={handleDelete}
														deleteMessage={{
															title: 'Excluir extensão de jornada',
															description: `Tem certeza que deseja excluir essa extensão de jornada?`,
														}}
														disableEdit={(rowData: any) =>
															!rowData.canEditOrDelete
														}
														disableDelete={(rowData: any) =>
															!rowData.canEditOrDelete
														}
														disableEditMessage={(rowData: any) =>
															rowData.canEditJustification ?? "Não é possível editar retroativamente"
														}
														disableDeleteMessage={(rowData: any) =>
															rowData.canDeleteJustification ?? "Não é possível excluir retroativamente"
														}
													/>
												</div>
											) : (
												<div className="flex justify-center p-10 font-graphie text-[13px] font-bold text-[#747474]">
													Ainda não existem extensões de jornadas planejadas
												</div>
											)}
											<div className="flex justify-end">
												<Button
													type="button"
													variant="link"
													className="w-[300px] bg-white font-semibold text-[#193CB9] underline hover:bg-[#e9eaf1]"
													onClick={() => {
														setType('create');
													}}
												>
													+ Adicionar nova extensão de jornada
												</Button>
											</div>
										</ExpandableSubtitle>
										<JourneyExtensionCard
											employeeId={employeeId}
											setUpdateData={setUpdateData}
											editData={editData}
											canSave={canSave}
										/>
									</>
								</div>
							</div>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
}
